import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiGetActivity,
  apiUndoActivity,
  ApiError,
  type ActivityActionDTO,
} from '../api';

/** Row label key for an action id: `team_member.delete` → `team_member_delete`.
 *  An action with no label (a value reserved for a later phase) falls back to
 *  `activity.actionLabel.unknown` — resolved at render via `i18n.exists`. */
function actionLabelKey(action: string): string {
  return `activity.actionLabel.${String(action || '').replace(/\./g, '_')}`;
}

/** SQLite `datetime('now')` is UTC without a zone marker → stamp it, and fall
 *  back to the raw string if it is not a parseable date (never print "Invalid
 *  Date"). */
function formatWhen(value: string): string {
  const raw = String(value || '');
  if (!raw) return '';
  const d = new Date(raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? raw : d.toLocaleString();
}

interface ActivityPanelProps {
  /** Band scope. The server drops rows belonging to bands the caller does not
   *  manage, so passing a band is a FILTER, never an authority check. */
  bandId?: string | null;
  /** Restrict to one person (a team member id) — used on a person's card. */
  entityId?: string;
  /** Called after a successful restore so the host page can reload its list. */
  onChanged?: () => void;
  /** False for a read-only viewer: the Undo buttons are then disabled (the
   *  server would answer 403 anyway). */
  canWrite?: boolean;
  limit?: number;
  /**
   * Reports whether the log holds at least one action (LOT E1): the hosting
   * card opens itself when it does. No extra fetch — the panel already loads
   * the list.
   */
  onHasDataChange?: (hasData: boolean) => void;
}

/**
 * Activity history + Undo (LOT B phase 1, 2026-10-15).
 *
 * Lists the recent destructive actions (from GET /api/activity) with one Undo
 * button per row; clicking it calls POST /api/activity/:id/undo and refreshes
 * the list. PHASE 1 covers DELETIONS only — undoing a field edit is phase 2 and
 * is announced as such by `activity.phaseNote`.
 *
 * The undo is idempotent server-side: a second click answers `already_exists`
 * and writes nothing. The client also guards against a double click (the button
 * is disabled while its own call is in flight).
 */
export default function ActivityPanel({
  bandId,
  entityId,
  onChanged,
  canWrite = true,
  limit = 25,
  onHasDataChange,
}: ActivityPanelProps) {
  const { t, i18n } = useTranslation();
  const [actions, setActions] = useState<ActivityActionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  // A Set, not the React state alone: two clicks landing in the SAME tick both
  // read the pre-render state. The ref is the actual in-flight guard.
  const inFlight = useRef<Set<number>>(new Set());

  // Report "this log has content" upward (LOT E1) without re-subscribing the
  // effect to a callback identity: the ref always holds the latest one.
  const onHasDataRef = useRef(onHasDataChange);
  onHasDataRef.current = onHasDataChange;
  const hasData = actions.length > 0;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    apiGetActivity({ band: bandId || undefined, entityId, limit })
      .then((res) => {
        if (cancelled) return;
        setActions(Array.isArray(res?.actions) ? res.actions : []);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [bandId, entityId, limit, reloadKey]);

  useEffect(() => { onHasDataRef.current?.(hasData); }, [hasData]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const undo = useCallback(async (row: ActivityActionDTO) => {
    if (inFlight.current.has(row.id)) return;
    inFlight.current.add(row.id);
    setPendingId(row.id);
    setNotice(null);
    try {
      const res = await apiUndoActivity(row.id);
      if (res?.status === 'already_exists') {
        // The entity was put back (by hand or by this very button twice):
        // nothing was written and nothing was overwritten.
        setNotice(t('activity.alreadyExists'));
      } else if (res?.status === 'conflict') {
        // LOT D: the row changed since that edit ⇒ nothing was written.
        setNotice(t('activity.undoConflict'));
      } else if (res?.status === 'not_found') {
        // LOT D: the edited row was deleted since — an edit undo never recreates.
        setNotice(t('activity.undoNotFound'));
      } else {
        setNotice(t('activity.undoDone', { name: res?.entry?.name || row.name }));
        onChanged?.();
      }
      reload();
    } catch (err) {
      // 422 = the snapshot has nothing left to restore, 404 = the row was
      // purged: both mean "refresh, there is nothing to undo here".
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 404 || status === 422) {
        reload();
      } else {
        setNotice(t('activity.undoError'));
      }
    } finally {
      inFlight.current.delete(row.id);
      setPendingId(null);
    }
  }, [t, onChanged, reload]);

  const label = (action: string) => {
    const key = actionLabelKey(action);
    return t(i18n.exists(key) ? key : 'activity.actionLabel.unknown');
  };

  return (
    <div data-activity-panel>
      <p style={{ color: 'var(--text-dim)', margin: '0 0 8px', fontSize: '0.88rem' }}>
        {t('activity.subtitle')}
      </p>
      {/* Phase-1 scope, always visible: deletions only (the card requires it). */}
      <p style={{ color: 'var(--text-dim)', margin: '0 0 10px', fontSize: '0.82rem' }}>
        {t('activity.phaseNote')}
      </p>

      {notice && (
        <p role="status" aria-live="polite" style={{ margin: '0 0 10px', fontSize: '0.88rem' }}>
          {notice}
        </p>
      )}

      {loading && <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>}

      {!loading && loadError && (
        <div>
          <p style={{ color: 'var(--danger)', margin: '0 0 8px', fontSize: '0.88rem' }}>
            {t('activity.loadError')}
          </p>
          <button className="btn btn-small" onClick={reload}>{t('team.retry')}</button>
        </div>
      )}

      {!loading && !loadError && actions.length === 0 && (
        <p style={{ color: 'var(--text-dim)', fontSize: '0.88rem' }}>{t('activity.empty')}</p>
      )}

      {!loading && !loadError && actions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {actions.map((row) => {
            const conflict = !row.undo?.available;
            return (
              <div
                key={row.id}
                data-activity-row={row.id}
                style={{
                  display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
                  borderTop: '1px solid var(--border)', paddingTop: 6,
                }}
              >
                <span style={{ fontSize: '0.88rem', minWidth: 0 }}>
                  <strong>{label(row.action)}</strong>
                  {row.name ? ` — ${row.name}` : ''}
                </span>
                <span style={{ color: 'var(--text-dim)', fontSize: '0.82rem' }}>
                  {(row.bands || []).map((b) => b.name).join(', ')}
                </span>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                  {formatWhen(row.created_at)}
                </span>
                {conflict ? (
                  // No undo offered: either the entity exists again (a deletion's
                  // undo would be a no-op) or, for an EDIT, the row changed since.
                  <span style={{ color: 'var(--text-dim)', fontSize: '0.82rem' }}>
                    {row.undo?.reason === 'changed_since' ? t('activity.undoConflict')
                      : (row.undo?.reason === 'row_deleted' ? t('activity.undoNotFound')
                        : t('activity.alreadyExists'))}
                  </span>
                ) : (
                  <button
                    className="btn btn-small"
                    disabled={!canWrite || pendingId === row.id}
                    onClick={() => undo(row)}
                  >
                    {pendingId === row.id ? t('activity.undoing') : t('activity.undo')}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ color: 'var(--text-muted)', margin: '10px 0 0', fontSize: '0.78rem' }}>
        {t('activity.retentionNote')}
      </p>
    </div>
  );
}
